<script>
import { useAlert } from 'dashboard/composables';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import AdvupDateTimeInput from 'dashboard/components-next/AdvupDateTimeInput.vue';
import { useNomeDoAgente } from 'dashboard/composables/useNomeDoAgente';

// advup — 2026-07-28 (pedido do Victor): botão manual "Colocar em
// follow-up" — substitui o antigo FollowUpModal (mensagem avulsa agendada
// na mão). Aqui a IA assume: dispara a cadência de verdade
// (FollowUpCadenceRun), com os mesmos passos/intervalos/guardrails que a
// detecção automática usa, só que sob demanda em vez de esperar o scan
// periódico — útil pra conversa onde a ÚLTIMA mensagem foi do lead (fora
// do alcance do job automático, que só pega quando a última é nossa).
export default {
  name: 'PlaceInFollowUpModal',
  components: {
    AdvupSelect,
    AdvupDateTimeInput,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ['close', 'started'],
  setup() {
    // O nome que o escritório deu ao agente (Lumos::NomeDoAgente).
    const { nomeDoAgente } = useNomeDoAgente();
    return { nomeDoAgente };
  },
  data() {
    return {
      selectedDefinitionId: null,
      isSubmitting: false,
      isLoadingDefinitions: false,
      // 'agora' = comportamento de sempre. 'data' = pedido da Dra. Jullya
      // (07/09/2026): o lead disse "agora não, me procura em outubro".
      quando: 'agora',
      dataEscolhida: '',
      // O que a regra automática barraria (lead frio, a vez é do escritório…). Vem do servidor (409); com eles na
      // tela, o botão vira "Colocar mesmo assim" (20/10/2026, Victor: "quero poder mandar quando eu quiser").
      avisos: [],
    };
  },
  computed: {
    currentChat() {
      return this.$store.getters.getSelectedChat;
    },
    conversationLabels() {
      return this.currentChat?.labels || [];
    },
    definitions() {
      return this.$store.getters['followUpCadences/getDefinitions'].filter(
        d => d.enabled
      );
    },
    detectedDefinition() {
      return this.definitions.find(d =>
        this.conversationLabels.includes(d.target_label)
      );
    },
    definitionOptions() {
      return this.definitions.map(d => ({
        value: d.id,
        label:
          d.id === this.detectedDefinition?.id
            ? `${d.name} (${this.$t('FOLLOW_UP_MODAL.DETECTED_BY_LABEL')})`
            : d.name,
      }));
    },
    selectedDefinition() {
      return this.definitions.find(d => d.id === this.selectedDefinitionId);
    },
    previewSteps() {
      const steps = this.selectedDefinition?.steps || [];
      return [...steps]
        .sort((a, b) => a.step_number - b.step_number)
        .slice(0, 3);
    },
    remainingStepsCount() {
      const total = this.selectedDefinition?.total_steps || 0;
      return Math.max(0, total - this.previewSteps.length);
    },
    agendado() {
      return this.quando === 'data';
    },
    podeEnviar() {
      if (!this.selectedDefinitionId) return false;
      return this.agendado ? !!this.dataEscolhida : true;
    },
    dataPorExtenso() {
      if (!this.dataEscolhida) return '';
      const [ano, mes, dia] = this.dataEscolhida.split('-');
      return `${dia}/${mes}/${ano}`;
    },
    lastStepDelayDays() {
      const steps = this.selectedDefinition?.steps || [];
      if (!steps.length) return 0;
      const maxMinutes = Math.max(...steps.map(s => s.delay_minutes || 0));
      return Math.round(maxMinutes / 60 / 24);
    },
  },
  watch: {
    show(newValue) {
      if (newValue) {
        // Modal reaberto herda o que ficou da vez anterior: sem isto, quem
        // agendou um lead pra outubro agenda o PRÓXIMO pra outubro sem
        // perceber, porque o campo continua preenchido.
        this.quando = 'agora';
        this.dataEscolhida = '';
        this.avisos = [];
        this.fetchDefinitions();
      }
    },
    detectedDefinition: {
      immediate: true,
      handler(definition) {
        if (definition) this.selectedDefinitionId = definition.id;
      },
    },
  },
  methods: {
    formatDelay(minutes) {
      if (minutes < 60)
        return this.$t('FOLLOW_UP_MODAL.DELAY_MINUTES', { count: minutes });
      if (minutes < 1440) {
        return this.$t('FOLLOW_UP_MODAL.DELAY_HOURS', {
          count: Math.round(minutes / 60),
        });
      }
      return this.$t('FOLLOW_UP_MODAL.DELAY_DAYS', {
        count: Math.round(minutes / 1440),
      });
    },
    async fetchDefinitions() {
      this.isLoadingDefinitions = true;
      try {
        await this.$store.dispatch('followUpCadences/fetchDefinitions');
      } catch {
        // ignore — tela mostra "nenhuma cadência" se ficar vazio
      } finally {
        this.isLoadingDefinitions = false;
      }
      this.focarCadencia();
    },
    // As cadências chegam DEPOIS de o modal abrir: na hora em que ele abre só
    // existe o Cancelar, e era nele que o foco ficava (05/10/2026). Quando a
    // lista chega, o foco vai para a escolha da cadência — a não ser que a
    // pessoa já esteja num campo.
    focarCadencia() {
      this.$nextTick(() => {
        const gatilho = this.$refs.cadencia?.querySelector('button');
        const ativo = document.activeElement;
        if (!gatilho || ativo?.matches?.('input, textarea, select')) return;
        gatilho.focus();
      });
    },
    async onSubmit() {
      if (!this.podeEnviar) return;

      this.isSubmitting = true;
      try {
        await this.$store.dispatch('followUpCadences/createRun', {
          conversationId: this.conversationId,
          followUpCadenceDefinitionId: this.selectedDefinitionId,
          startOn: this.agendado ? this.dataEscolhida : null,
          mesmoAssim: this.avisos.length > 0,
        });
        useAlert(
          this.agendado
            ? this.$t('FOLLOW_UP_MODAL.SUCCESS_SCHEDULED', {
                date: this.dataPorExtenso,
              })
            : this.$t('FOLLOW_UP_MODAL.SUCCESS')
        );
        this.$emit('started');
        this.$emit('close');
      } catch (error) {
        const corpo = error?.response?.data;
        if (error?.response?.status === 409 && corpo?.precisa_confirmar) {
          this.avisos = corpo.avisos || [];
          return;
        }
        useAlert(corpo?.error || this.$t('FOLLOW_UP_MODAL.ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <!-- A casca é a do produto (`.advup-modal`), a mesma de "Agendar
         atendimento" e "Transferir": era o modal antigo do Chatwoot, com o
         topo e os botões dele. Íris no topo porque quem faz o follow-up é a IA. -->
    <Transition name="advup-saida">
      <div v-if="show" class="advup-modal-fundo" @click.self="$emit('close')">
        <div
          v-modal-acessivel="() => $emit('close')"
          class="advup-modal advup-modal--claro advup-modal--estreito advup-modal--teto"
        >
          <div class="advup-modal__topo advup-modal__topo--iris">
            <!-- Topo claro da Agenda (§5.10), como nos outros módulos — era o
                 degradê escuro (05/10/2026). -->
            <i class="advup-modal__marca i-lucide-repeat" aria-hidden="true" />
            <span class="advup-modal__selo" aria-hidden="true">
              <i class="i-lucide-repeat" />
            </span>
            <div class="min-w-0 flex-1">
              <h2 class="advup-modal__titulo">
                {{ $t('FOLLOW_UP_MODAL.TITLE') }}
              </h2>
              <p class="advup-modal__sub advup-modal__sub--solta">
                {{ $t('FOLLOW_UP_MODAL.DESCRIPTION') }}
              </p>
            </div>
            <button
              type="button"
              class="advup-modal__fechar"
              :aria-label="$t('FOLLOW_UP_MODAL.CANCEL')"
              @click="$emit('close')"
            >
              <i class="i-lucide-x" />
            </button>
          </div>

          <div class="advup-modal__corpo pfu-corpo">
            <!-- Só depois de carregar: antes, "nenhuma cadência" piscava na
                 abertura de todo modal. -->
            <p
              v-if="!definitions.length && !isLoadingDefinitions"
              class="advup-nota"
            >
              {{ $t('FOLLOW_UP_MODAL.NO_DEFINITIONS') }}
            </p>
            <template v-else-if="definitions.length">
              <div ref="cadencia">
                <span class="advup-rotulo" aria-hidden="true">
                  {{ $t('FOLLOW_UP_MODAL.CADENCE_LABEL') }}
                </span>
                <AdvupSelect
                  v-model="selectedDefinitionId"
                  :options="definitionOptions"
                  :rotulo="$t('FOLLOW_UP_MODAL.CADENCE_LABEL')"
                  min-width="100%"
                />
              </div>

              <div>
                <span class="advup-rotulo" aria-hidden="true">
                  {{ $t('FOLLOW_UP_MODAL.WHEN_LABEL') }}
                </span>
                <div
                  class="advup-seg pfu-quando"
                  role="radiogroup"
                  :aria-label="$t('FOLLOW_UP_MODAL.WHEN_LABEL')"
                >
                  <button
                    type="button"
                    role="radio"
                    class="advup-seg__btn"
                    :class="{ 'advup-seg__btn--on': !agendado }"
                    :aria-checked="!agendado"
                    @click="quando = 'agora'"
                  >
                    <i class="i-lucide-zap" aria-hidden="true" />
                    {{ $t('FOLLOW_UP_MODAL.WHEN_NOW') }}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    class="advup-seg__btn"
                    :class="{ 'advup-seg__btn--on': agendado }"
                    :aria-checked="agendado"
                    @click="quando = 'data'"
                  >
                    <i class="i-lucide-calendar-days" aria-hidden="true" />
                    {{ $t('FOLLOW_UP_MODAL.WHEN_DATE') }}
                  </button>
                </div>

                <!-- `no-past` porque agendar pra ontem é sempre erro de
                       digitação, e o backend recusaria depois de o modal já ter
                       fechado. -->
                <Transition name="pfu-abre">
                  <div v-if="agendado" class="pfu-data">
                    <AdvupDateTimeInput
                      v-model="dataEscolhida"
                      date-only
                      no-past
                    />
                    <p class="advup-nota pfu-nota">
                      {{
                        $t('FOLLOW_UP_MODAL.WHEN_DATE_HINT', {
                          agente: nomeDoAgente,
                        })
                      }}
                    </p>
                  </div>
                </Transition>
              </div>

              <!-- A prévia é a IA dizendo o que vai fazer: uma linha do tempo
                     dos passos, na ordem em que vão acontecer. -->
              <div v-if="selectedDefinition" class="pfu-previa">
                <p class="pfu-previa__topo">
                  <i
                    class="i-lucide-sparkles pfu-previa__faisca"
                    aria-hidden="true"
                  />
                  {{ $t('FOLLOW_UP_MODAL.AI_HANDLES') }}
                </p>
                <!-- Sem isto a prévia mente: "Passo 2 em 3 dias" passa a
                       contar da data escolhida, não de hoje. -->
                <p v-if="agendado && dataPorExtenso" class="pfu-previa__base">
                  {{
                    $t('FOLLOW_UP_MODAL.PREVIEW_FROM_DATE', {
                      date: dataPorExtenso,
                    })
                  }}
                </p>
                <ol class="pfu-passos">
                  <li
                    v-for="(step, i) in previewSteps"
                    :key="step.step_number"
                    class="pfu-passo advup-entra"
                    :style="{ '--i': i }"
                  >
                    <span class="pfu-passo__marca" aria-hidden="true" />
                    <span class="pfu-passo__nome">
                      {{
                        $t('FOLLOW_UP_MODAL.STEP', { number: step.step_number })
                      }}
                    </span>
                    <span class="pfu-passo__quando">
                      {{ formatDelay(step.delay_minutes) }}
                    </span>
                  </li>
                </ol>
                <p v-if="remainingStepsCount > 0" class="pfu-previa__mais">
                  {{
                    $t('FOLLOW_UP_MODAL.MORE_STEPS', {
                      count: remainingStepsCount,
                      days: lastStepDelayDays,
                    })
                  }}
                </p>
              </div>

              <!-- Âmbar é atenção no produto: o follow-up para sozinho quando
                     o cliente responde, e isso precisa estar dito. -->
              <p class="pfu-aviso">
                <i class="i-lucide-info pfu-aviso__ico" aria-hidden="true" />
                <span>{{ $t('FOLLOW_UP_MODAL.STOPS_AUTOMATICALLY') }}</span>
              </p>

              <!-- O que a regra automática barraria: a pessoa lê e decide. -->
              <section v-if="avisos.length" class="pfu-decisao" role="alert">
                <p class="pfu-decisao__tit">
                  <i class="i-lucide-triangle-alert" aria-hidden="true" />
                  {{ $t('FOLLOW_UP_MODAL.AVISOS_TITULO') }}
                </p>
                <ul class="pfu-decisao__lista">
                  <li v-for="aviso in avisos" :key="aviso">{{ aviso }}</li>
                </ul>
                <p class="pfu-decisao__nota">
                  {{ $t('FOLLOW_UP_MODAL.AVISOS_NOTA') }}
                </p>
              </section>
            </template>
          </div>

          <div class="advup-modal__rodape advup-modal__rodape--acoes">
            <button
              type="button"
              class="advup-btn advup-btn--vazado"
              @click="$emit('close')"
            >
              {{ $t('FOLLOW_UP_MODAL.CANCEL') }}
            </button>
            <button
              type="button"
              class="advup-btn-forte"
              :disabled="!podeEnviar || isSubmitting"
              @click="onSubmit"
            >
              <i
                :class="
                  isSubmitting
                    ? 'i-lucide-loader-2 animate-spin'
                    : agendado
                      ? 'i-lucide-calendar-check'
                      : 'i-lucide-play'
                "
                aria-hidden="true"
              />
              {{
                avisos.length
                  ? $t('FOLLOW_UP_MODAL.SUBMIT_MESMO_ASSIM')
                  : agendado
                    ? $t('FOLLOW_UP_MODAL.SUBMIT_SCHEDULED')
                    : $t('FOLLOW_UP_MODAL.SUBMIT')
              }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.pfu-decisao {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  padding: var(--esp-3) var(--esp-4);
  border: 1px solid rgb(var(--amber-6));
  border-radius: var(--r-3);
  background: rgb(var(--amber-2));
  color: rgb(var(--amber-12));
}

.pfu-decisao__tit {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  font-weight: var(--peso-forte);
}

.pfu-decisao__lista {
  margin: 0;
  padding-left: 1.25rem;
  list-style: disc;
  font-size: var(--txt-corpo);
  line-height: 1.5;
}

.pfu-decisao__nota {
  margin: 0;
  font-size: var(--txt-nota);
  color: rgb(var(--amber-11));
}

.pfu-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}

.pfu-quando {
  width: 100%;
}

.pfu-quando > .advup-seg__btn {
  flex: 1;
  gap: var(--esp-1);
}

.pfu-data {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin-top: var(--esp-2);
}

.pfu-nota {
  margin: 0;
}

.pfu-abre-enter-active,
.pfu-abre-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva);
}

.pfu-abre-enter-from,
.pfu-abre-leave-to {
  opacity: 0;
  translate: 0 -4px;
}

/* ── A prévia (íris = IA) ─────────────────────────────────────────────── */
.pfu-previa {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  padding: var(--esp-3) var(--esp-4);
  border: 1px solid rgb(var(--iris-5));
  border-radius: var(--r-4);
  background: rgb(var(--iris-2));
}

.pfu-previa__topo {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  font-size: var(--txt-selo);
  font-weight: var(--peso-forte);
  color: rgb(var(--iris-11));
}

/* A faísca respira devagar: a prévia é o único ponto do modal que muda
   sozinho quando a pessoa troca a cadência, e o movimento diz isso. */
.pfu-previa__faisca {
}

.pfu-previa__base,
.pfu-previa__mais {
  margin: 0;
  font-size: var(--txt-nota);
  color: rgb(var(--iris-11));
}

.pfu-previa__mais {
  color: rgb(var(--slate-11));
}

/* Os passos são uma LINHA DO TEMPO: um fio íris liga as marcas, porque a
   ordem é a informação — o passo 2 só acontece depois do 1. */
.pfu-passos {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.pfu-passos::before {
  content: '';
  position: absolute;
  top: var(--esp-2);
  bottom: var(--esp-2);
  left: 4px;
  width: 2px;
  border-radius: var(--r-redondo);
  background: rgb(var(--iris-5));
}

.pfu-passo {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  font-size: var(--txt-nota);
}

.pfu-passo__marca {
  position: relative;
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border: 2px solid rgb(var(--iris-9));
  border-radius: var(--r-redondo);
  background: rgb(var(--solid-1));
}

.pfu-passo:first-child .pfu-passo__marca {
  background: rgb(var(--iris-9));
}

.pfu-passo__nome {
  flex: 1;
  color: rgb(var(--slate-11));
}

.pfu-passo__quando {
  font-weight: var(--peso-rotulo);
  color: rgb(var(--slate-12));
  font-variant-numeric: tabular-nums;
}

/* ── O aviso (âmbar = atenção) ────────────────────────────────────────── */
.pfu-aviso {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-3);
  border: 1px solid rgb(var(--amber-5));
  border-radius: var(--r-4);
  background: rgb(var(--amber-2));
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--amber-11));
}

.pfu-aviso__ico {
  width: var(--ico-1);
  height: var(--ico-1);
  flex-shrink: 0;
  /* Centrado na PRIMEIRA linha do texto, que pode quebrar em duas. */
  margin-top: calc((1lh - var(--ico-1)) / 2);
}

@media (prefers-reduced-motion: reduce) {
  .pfu-previa__faisca {
    animation: none;
  }
}
</style>
